import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api } from '../api';
import { Field, Problems } from '../components/Field';
import { currentLocale, dayLabel, todayLocal } from '../dates';
import { describe } from '../errors';
import { BILL_PILL, BILL_STATUS } from '../pages/billShared';
import { money } from './format';
import { isOffline, refreshAll, usePortalLoad } from './offline';
import { Actions, LoadFailure, SavedNote } from './shared';

interface ToBill {
  id: number;
  date: string;
  customer_name: string;
  /** What the visit pays, ex GST; null where the business doesn't show contractors their pay. */
  pay_amount: string | null;
}
interface Bill {
  id: number;
  invoice_number: string;
  invoice_date: string | null;
  total_ex_gst: string;
  gst_amount: string;
  status: string;
  paid_at: string | null;
}
interface Loaded {
  sees_pay: boolean;
  gst_applicable: boolean;
  visits: ToBill[];
  bills: Bill[];
}

/** What each status means for the contractor, after the status itself (the office's words, one vocabulary). */
const MEANS: Record<string, string> = {
  received: 'The office is checking it.',
  matched: 'Waiting to be paid.',
  disputed: 'The office will be in touch.',
  paid: '',
};

const cents = (x: string) => Math.round(Number(x || 0) * 100);
const typed = (s: string) =>
  s.trim() !== '' && Number.isFinite(Number(s)) ? Math.round(Number(s) * 100) : null;
/** The day a bill was paid, on this phone's calendar (paid_at is a moment, not a date). */
const paidDay = (iso: string) =>
  new Date(iso).toLocaleDateString(currentLocale(), {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });

/**
 * Bill the office for the visits you led: tick them, give your invoice's number and total,
 * and send it. The office checks it against the visits, then pays it. Sending needs a signal.
 */
export function PortalBills() {
  const load = usePortalLoad<Loaded>('/api/portal/bills');
  const { data } = load;
  const [unticked, setUnticked] = useState<number[]>([]);
  const [number, setNumber] = useState('');
  const [date, setDate] = useState(todayLocal());
  const [total, setTotal] = useState<string | null>(null);
  const [gst, setGst] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [done, setDone] = useState('');
  const [busy, setBusy] = useState(false);
  const doneNote = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (done) doneNote.current?.focus();
  }, [done]);

  const head = (
    <>
      <h1>Bills</h1>
      <p className="muted p-sub">
        Bill the office for the visits you led. They check it against the visits, then pay it.
      </p>
    </>
  );
  if (!data) {
    return (
      <>
        {head}
        {load.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <LoadFailure what="your bills" load={load} />
        )}
      </>
    );
  }

  const picked = data.visits.filter((v) => !unticked.includes(v.id));
  // With pay shown, the total starts as what the ticked visits pay; otherwise it is theirs to give.
  const owed = data.sees_pay ? picked.reduce((s, v) => s + cents(v.pay_amount ?? '0'), 0) : null;
  const billed = total === null ? owed : typed(total);
  const totalShown = total ?? (owed === null ? '' : (owed / 100).toFixed(2));
  // Not registered for GST: there is none to give, so no box to get wrong.
  const gstShown = !data.gst_applicable
    ? '0.00'
    : (gst ?? (billed !== null ? (Math.round(billed / 10) / 100).toFixed(2) : '0.00'));
  const gstCents = typed(gstShown);
  const diff = owed !== null && billed !== null ? billed - owed : 0;
  const count = `${picked.length} ${picked.length === 1 ? 'visit' : 'visits'}`;
  const sum = billed !== null && gstCents !== null ? billed + gstCents : null;

  async function send(e: FormEvent) {
    e.preventDefault();
    setProblems([]);
    setDone('');
    // Said here, by the box, rather than after a trip to the server.
    if (billed === null || gstCents === null) {
      setFields({
        ...(billed === null && { total_ex_gst: 'Give the total before GST, for example 360.00.' }),
        ...(gstCents === null && { gst_amount: 'Give the GST, or 0.00.' }),
      });
      return;
    }
    setBusy(true);
    setFields({});
    try {
      const r = await api<{ invoice_number: string }>('POST', '/api/portal/bills', {
        invoice_number: number,
        invoice_date: date,
        total_ex_gst: totalShown,
        gst_amount: gstShown,
        visit_ids: picked.map((v) => v.id),
      });
      setDone(`Bill ${r.invoice_number} sent to the office. They'll check it, then pay it.`);
      setUnticked([]);
      setNumber('');
      setTotal(null);
      setGst(null);
      refreshAll();
    } catch (err) {
      if (isOffline(err)) {
        setProblems([
          'No signal. Everything you typed is still here: send it again when you have a signal.',
        ]);
        return;
      }
      const d = describe(err);
      setProblems(d.problems);
      setFields(d.fields);
    } finally {
      setBusy(false);
    }
  }

  async function takeBack(b: Bill) {
    setProblems([]);
    setDone('');
    try {
      await api('DELETE', `/api/portal/bills/${b.id}`);
      setDone(`Bill ${b.invoice_number} taken back. Its visits are ready to bill again.`);
      refreshAll();
    } catch (err) {
      setProblems(describe(err).problems);
    }
  }

  return (
    <>
      {head}
      <SavedNote load={load} extra="You need a signal to send a bill." />

      <h2>To bill</h2>
      {data.visits.length === 0 ? (
        <p className="empty">Nothing to bill: every visit you led is on a bill.</p>
      ) : (
        <form onSubmit={send} className="stack" noValidate aria-label="New bill">
          {fields.visits && <p className="error">{fields.visits}</p>}
          <fieldset className="p-bill-visits">
            <legend>The visits it covers</legend>
            <label className="check">
              <input
                type="checkbox"
                checked={unticked.length === 0}
                onChange={() =>
                  setUnticked(unticked.length === 0 ? data.visits.map((v) => v.id) : [])
                }
              />
              All {data.visits.length} visits
            </label>
            <ul className="p-list">
              {data.visits.map((v) => (
                <li key={v.id} className="p-bill-visit">
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={!unticked.includes(v.id)}
                      onChange={() =>
                        setUnticked((u) =>
                          u.includes(v.id) ? u.filter((x) => x !== v.id) : [...u, v.id],
                        )
                      }
                    />
                    <span>
                      <strong>{dayLabel(v.date)}</strong> {v.customer_name}
                    </span>
                  </label>
                  {v.pay_amount !== null && (
                    <span className="p-bill-pay">{money(v.pay_amount)}</span>
                  )}
                </li>
              ))}
            </ul>
          </fieldset>
          <Field
            label="Your invoice number"
            hint="Leave it blank if you don't number your invoices: the bill is numbered by its date."
            error={fields.invoice_number}
          >
            <input value={number} maxLength={50} onChange={(e) => setNumber(e.target.value)} />
          </Field>
          <Field label="Date" error={fields.invoice_date}>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Field
            label="Total before GST"
            hint={data.sees_pay && total === null ? 'What the ticked visits pay.' : undefined}
            error={fields.total_ex_gst}
          >
            <input
              inputMode="decimal"
              value={totalShown}
              onChange={(e) => {
                setTotal(e.target.value);
                setFields((f) => ({ ...f, total_ex_gst: '' }));
              }}
            />
          </Field>
          {data.gst_applicable ? (
            <Field
              label="GST"
              hint={gst === null ? '10% of the total.' : undefined}
              error={fields.gst_amount}
            >
              <input
                inputMode="decimal"
                value={gstShown}
                onChange={(e) => {
                  setGst(e.target.value);
                  setFields((f) => ({ ...f, gst_amount: '' }));
                }}
              />
            </Field>
          ) : (
            <p className="muted">
              No GST: you are not registered for GST. If that has changed, tell the office.
            </p>
          )}
          {owed !== null && billed !== null && diff !== 0 && (
            <p className="p-note" aria-live="polite">
              The ticked visits pay {money(owed / 100)}: this bill is {money(Math.abs(diff) / 100)}{' '}
              {diff > 0 ? 'more' : 'less'}. The office will check it with you.
            </p>
          )}
          <Actions>
            <button type="submit" className="p-big" disabled={busy || picked.length === 0}>
              {busy
                ? 'Sending…'
                : sum === null
                  ? `Send bill for ${count}`
                  : `Send bill: ${count}, ${money(sum / 100)}`}
            </button>
          </Actions>
          <p className="muted">You can take it back until the office checks it.</p>
        </form>
      )}
      {/* Said by the button that was pressed, and moved to so it is read out. */}
      <Problems problems={problems} title="Not sent." focus />
      {done && (
        <p role="status" className="p-done" ref={doneNote} tabIndex={-1}>
          {done}
        </p>
      )}

      {data.bills.length > 0 && (
        <>
          <h2>Your bills</h2>
          <ul className="p-list">
            {data.bills.map((b) => (
              <li key={b.id} className="p-visit p-bill">
                <strong>
                  {b.invoice_number} · {money((cents(b.total_ex_gst) + cents(b.gst_amount)) / 100)}
                </strong>
                <span className="p-bill-status">
                  <span className={BILL_PILL[b.status] ?? 'pill'}>
                    {BILL_STATUS[b.status] ?? b.status}
                  </span>
                  <span className="muted">
                    {b.status === 'paid' && b.paid_at
                      ? `on ${paidDay(b.paid_at)}`
                      : b.invoice_date
                        ? dayLabel(b.invoice_date)
                        : ''}
                    {MEANS[b.status] ? ` · ${MEANS[b.status]}` : ''}
                  </span>
                </span>
                {b.status === 'received' && (
                  <Actions>
                    <button type="button" className="quiet" onClick={() => void takeBack(b)}>
                      Take back <span className="sr-only">{b.invoice_number}</span>
                    </button>
                  </Actions>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}
